import React, { useState, useEffect } from 'react';
import { supabase } from 'lib/supabase';
import Header from 'components/ui/Header';
import AnalyticsMetrics from './components/AnalyticsMetrics';
import PerformanceCharts from './components/PerformanceCharts';
import LinkPerformanceTable from './components/LinkPerformanceTable';
import AnalyticsFilters from './components/AnalyticsFilters';
import ExportPanel from './components/ExportPanel';
import { Helmet } from 'react-helmet';

const LinkAnalytics = () => {
  const [_user, setUser] = useState(null); // _user pour éviter unused
  const [trackingLinks, setTrackingLinks] = useState([]);
  const [clickData, setClickData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    dateRange: '30', // last 30 days
    campaign: '',
    status: 'active'
  });

  useEffect(() => {
    const fetchAnalyticsData = async () => {
      try {
        // Get current user
  const { data: { session }, error: authError } = await supabase.auth.getSession();
        if (authError) {
          console.error('Auth error:', authError);
          return;
        }

        setUser(session?.user || null);

        if (session?.user) {
          // Calculate date range
          // endDate non utilisé retiré pour lint
          // const endDate = new Date();
          const startDate = new Date();
          startDate?.setDate(startDate?.getDate() - parseInt(filters?.dateRange));

          // Fetch tracking links with analytics data
          let query = supabase
            .from('tracking_links')
            .select(`
              id, title, description, short_url, target_url,
              total_clicks, unique_clicks, status, created_at,
              utm_source, utm_medium, utm_campaign,
              campaigns(name)
            `)
            .eq('created_by', session?.user?.id)
            .gte('created_at', startDate.toISOString());

          if (filters?.status && filters?.status !== '') {
            query = query.eq('status', filters.status);
          }

          if (filters?.campaign && filters?.campaign !== '') {
            query = query.eq('campaign_id', filters.campaign);
          }

          const { data: linksData, error: linksError } = await query.order('created_at', { ascending: false });

          if (linksError) {
            console.error('Links error:', linksError);
          } else {
            setTrackingLinks(linksData || []);
          }

          // Fetch click analytics for charts
          if (linksData && linksData?.length > 0) {
            const linkIds = linksData?.map(link => link?.id);
            const { data: clicksData, error: clicksError } = await supabase
              .from('click_analytics')
              .select(`
                id, tracking_link_id, source, device_type,
                browser, os, country, city, clicked_at
              `)
              .in('tracking_link_id', linkIds)
              .gte('clicked_at', startDate.toISOString())
              .order('clicked_at', { ascending: false });

            if (clicksError) {
              console.error('Clicks error:', clicksError);
            } else {
              setClickData(clicksData || []);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching analytics data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalyticsData();
  }, [filters]);

  const handleFiltersChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  // Calculate summary metrics
  const summaryMetrics = {
    totalClicks: trackingLinks?.reduce((sum, link) => sum + (link?.total_clicks || 0), 0) || 0,
    uniqueVisitors: trackingLinks?.reduce((sum, link) => sum + (link?.unique_clicks || 0), 0) || 0,
    activeLinks: trackingLinks?.filter(link => link?.status === 'active')?.length || 0,
    conversionRate: 0 // Could be calculated based on additional data
  };

  if (loading) {
    return (
  <div className="min-h-screen bg-gray-50 dark:bg-neutral-950">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Chargement des analyses...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
  <div className="min-h-screen bg-gray-50 dark:bg-neutral-950 text-gray-900 dark:text-neutral-100">
      <Helmet>
        <title>Analyses des Liens - Artisio</title>
        <meta name="description" content="Analysez les performances de vos liens de suivi et QR codes avec des métriques détaillées et des rapports complets." />
      </Helmet>
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-neutral-100">Analyses des Liens</h1>
          <p className="mt-2 text-lg text-gray-600 dark:text-neutral-400">
            Suivez les performances de vos campagnes et liens de promotion
          </p>
        </div>

        {/* Filters */}
        <div className="mb-6">
          <AnalyticsFilters
            filters={filters}
            onFiltersChange={handleFiltersChange}
          />
        </div>

        {/* Metrics Cards */}
        <div className="mb-8">
          <AnalyticsMetrics
            metrics={summaryMetrics}
            trackingLinks={trackingLinks}
            clickData={clickData}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content Area - Charts and Tables */}
          <div className="lg:col-span-2 space-y-6">
            <PerformanceCharts
              trackingLinks={trackingLinks}
              clickData={clickData}
              dateRange={filters?.dateRange}
            />
            
            <LinkPerformanceTable
              trackingLinks={trackingLinks}
              clickData={clickData}
            />
          </div>

          {/* Sidebar - Export and Additional Info */}
          <div className="lg:col-span-1 space-y-6">
            <ExportPanel
              trackingLinks={trackingLinks}
              clickData={clickData}
              filters={filters}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LinkAnalytics;